import { useEffect, useState, useLayoutEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, X, Rocket } from "lucide-react";
import { AriaMark } from "@/components/AriaMark";

/* ---------- DOM helpers used by step actions ---------- */
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const waitFor = async (sel, tries = 30) => {
  for (let i = 0; i < tries; i++) {
    const el = document.querySelector(sel);
    if (el) return el;
    await sleep(100);
  }
  return null;
};
const clickIt = async (sel, tries = 30) => { (await waitFor(sel, tries))?.click(); };
const clickOpt = (sel) => clickIt(sel, 3); // optional click — don't wait long
const pressEsc = () => document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", code: "Escape", keyCode: 27, bubbles: true }));
const openAria = async () => { window.dispatchEvent(new Event("aria:open")); await waitFor('[data-testid="ask-aria-panel"]', 15); };
const closeAria = async () => { await clickOpt('[data-testid="ask-aria-tab-chat"]'); await clickOpt('[data-testid="ask-aria-close"]'); };
const closeDrawer = async () => {
  pressEsc();
  await sleep(250);
  await clickOpt('[data-testid="expense-detail-drawer"] > button.absolute');
};
const selectTwoRows = async () => {
  await waitFor('[data-testid^="expenses-row-checkbox-"]');
  for (let i = 0; i < 2; i++) {
    // re-query each time — selection state re-renders the rows
    const boxes = [...document.querySelectorAll('[data-testid^="expenses-row-checkbox-"]')];
    boxes[i]?.click();
    await sleep(180);
  }
};
const openFirstEmployee = async () => {
  if (document.querySelector('[data-testid="employee-thread"]')) return;
  const row = (await waitFor('[data-testid^="employee-row-"]', 12)) || (await waitFor('[data-testid^="employee-card-"]', 8));
  row?.click();
};

/* ---------- The script: 30 steps through every corner of the product ---------- */
const STEPS = [
  { intro: true, title: "Meet Aria", body: "GoodMagic's finance agent chases receipts, files expenses and enforces policy — on autopilot. This is the full tour: every corner of the product in about three minutes. Aria will drive; you just click Next." },

  /* — Auto-chase — */
  { route: "/communications", target: "chase-stats", placement: "bottom", eyebrow: "Auto-chase · 1/5",
    title: "Mission control for missing receipts", body: "Live counters: how many people Aria is chasing, dollars outstanding, receipts recovered this month, and every nudge sent in the last week — across Slack, Teams, email and text." },
  { selector: '[data-testid^="chase-row-"]', placement: "bottom", eyebrow: "Auto-chase · 2/5",
    title: "Anatomy of a chase", body: "Each row is one employee: what they owe and which merchants, the playbook they're on, when the next nudge lands, and the last time Aria touched them." },
  { selector: '[data-testid="chase-step-chain"]', placement: "bottom", eyebrow: "Auto-chase · 3/5",
    title: "The playbook, step by step", body: "Solid icons already fired, the pulsing one is up next, dim ones are queued — with the wait time between each. Timers restart every time Aria makes contact." },
  { selector: '[data-testid^="chase-nudge-"]', placement: "left", eyebrow: "Auto-chase · 4/5",
    title: "Jump the queue anytime", body: "\u201cNudge now\u201d sends the next message immediately — Aria writes it, references the exact receipts owed, and resets the timers." },
  { selector: '[data-testid^="chase-escalated-"]', placement: "left", eyebrow: "Auto-chase · 5/5",
    title: "Humans only when it matters", body: "When a playbook runs out of steps, the chase escalates to you. Everything else, Aria handles alone." },

  /* — Strategies — */
  { route: "/communications?tab=strategies", target: "strategies-grid", placement: "top", eyebrow: "Strategies",
    title: "The playbook library", body: "Gentle nudge, standard follow-up, hard escalation — one is the workspace default, and any employee can be put on a stricter one." },
  { route: "/communications?tab=strategies", action: () => clickOpt('[data-testid="strategy-new-button"]'),
    target: "strategy-palette", placement: "right", eyebrow: "Strategies",
    title: "Build one from blocks", body: "Channels (Slack, Teams, email, text, call), tones from friendly to urgent, and wait durations — all draggable blocks." },
  { target: "strategy-sequence", placement: "left", eyebrow: "Strategies",
    title: "Drag, drop, done", body: "Compose the escalation sequence and save. Aria follows it to the letter for every assigned employee — no code, no automations to wire up." },

  /* — People — */
  { route: "/communications?tab=employees", target: "employees-view-toggle", placement: "bottom", eyebrow: "Your team",
    title: "The directory", body: "Everyone on spend, as a table or cards — with compliance flags, spend scores and their assigned chase playbook." },
  { target: "employees-search-input", placement: "bottom", eyebrow: "Your team",
    title: "Find anyone fast", body: "Search by name, role or department. Aria can also filter this list for you — try asking it for \u201ceveryone with missing receipts\u201d." },
  { action: openFirstEmployee, target: "employee-thread", placement: "right", eyebrow: "Your team",
    title: "One thread per person", body: "Slack, Teams, email and text — the entire conversation with Aria in a single thread. Jump in and take over from the agent whenever you want." },
  { target: "employee-strategy-chip", placement: "bottom", eyebrow: "Your team",
    title: "Their playbook, right here", body: "See and switch which escalation strategy this person is on — repeat offenders can graduate to the hard version in one click." },

  /* — Ask Aria — */
  { route: "/communications", action: openAria, target: "ask-aria-panel", placement: "left", eyebrow: "Ask Aria",
    title: "The copilot itself", body: "Aria rides along on every page and knows what you're looking at. Ask questions in plain English or give it work: approve, reject, remind, export, filter…" },
  { target: "aria-suggestions", placement: "left", eyebrow: "Ask Aria",
    title: "Page-aware suggestions", body: "One-tap prompts that change based on where you are — on Communications it offers receipt chasing, on Expenses it offers approvals." },
  { target: "aria-workflows", placement: "left", eyebrow: "Ask Aria",
    title: "Replayable workflows", body: "Run a few commands, then save the session as a named workflow — \u201cWeekly cleanup\u201d replays every step with one click." },
  { target: "ask-aria-select-toggle", placement: "left", eyebrow: "Ask Aria",
    title: "Select things on screen", body: "Selection mode turns your cursor into a highlighter: click expenses, people or queues anywhere in the app, then tell Aria what to do with them — \u201capprove these\u201d, \u201cremind them\u201d." },
  { action: () => clickIt('[data-testid="ask-aria-tab-tasks"]', 10), target: "ask-aria-tasks-panel", placement: "left", eyebrow: "Ask Aria",
    title: "Watch it work", body: "Every command becomes a live task you can watch run and complete — plus Aria's own background queue." },
  { action: () => clickIt('[data-testid="ask-aria-tab-audit"]', 10), target: "ask-aria-audit-panel", placement: "left", eyebrow: "Ask Aria",
    title: "A full paper trail", body: "Everything the agent has ever done — queries, actions, navigations — timestamped and exportable to CSV. Autonomy with receipts." },

  /* — Expenses — */
  { route: "/expenses", action: closeAria, target: "expenses-mode-switch", placement: "bottom", eyebrow: "Expenses",
    title: "Active work vs. archive", body: "One switch flips between the live queue and cold storage — exported history and the not-yet-exported batch." },
  { target: "expenses-view-switch", placement: "bottom", eyebrow: "Expenses",
    title: "Table or kanban", body: "Work the queue as a dense table or a drag-and-drop board — and set whichever you prefer as your default." },
  { target: "expenses-search-input", placement: "bottom", eyebrow: "Expenses",
    title: "Search everything", body: "Merchants, employees, GL codes — press \u201c/\u201d anywhere to jump here. Combine with per-column filters straight from the table headers." },
  { target: "views-menu-button", placement: "bottom", eyebrow: "Expenses",
    title: "Saved views", body: "Any filter combination can be saved and reused. When Aria builds a filtered view for you, it offers to save it too." },
  { target: "assigned-view-toggle", placement: "bottom", eyebrow: "Expenses",
    title: "Split the workload", body: "Assign expenses to teammates and flip on \u201cAssigned to\u201d to group the table by owner — instant triage lanes." },
  { target: "columns-button", placement: "left", eyebrow: "Expenses",
    title: "Your table, your columns", body: "Hide, show and drag columns into whatever order fits your workflow — it sticks between sessions." },
  { action: selectTwoRows, target: "bulk-toolbar", placement: "top", eyebrow: "Expenses",
    title: "Bulk operations", body: "Select rows and a floating toolbar appears: approve, reject, export, message the employees, or assign to a teammate — without the table ever shifting." },
  { action: async () => { await clickOpt('[data-testid="bulk-clear-button"]'); await clickIt('[data-testid^="expenses-table-row-"]'); },
    target: "expense-detail-drawer", placement: "left", eyebrow: "Expenses",
    title: "The full dossier", body: "Receipt image, policy checks, GL coding, comments and a complete audit timeline — everything about one expense in one drawer." },
  { target: "drawer-generate-summary", placement: "left", eyebrow: "Expenses",
    title: "AI audit context", body: "One click and Aria writes the audit summary: what was bought, why it's in or out of policy, and what happened since — ready for your auditors." },
  { action: async () => { await closeDrawer(); await clickIt('[data-testid="expenses-view-kanban"]', 10); },
    selector: '[data-testid^="kanban-column-"]', placement: "right", eyebrow: "Expenses",
    title: "The approval board", body: "Aria pre-sorts every expense into Needs review, Waiting and Approved. Drag a card to decide — it files the rest." },
  { action: async () => { await clickIt('[data-testid="expenses-view-table"]', 10); await clickIt('[data-testid="expenses-mode-archive"]', 10); },
    target: "archive-view", placement: "top", eyebrow: "Expenses",
    title: "Archive & data movement", body: "Approved expenses flow downstream on your schedule — real-time, hourly or nightly export to your ERP, with a live countdown. Restore anything with one click." },

  /* — Policies — */
  { route: "/policies", target: "policy-builder-input", placement: "bottom", eyebrow: "Policies",
    title: "Policy in plain English", body: "Type a rule the way you'd say it out loud — or attach a photo or PDF of your travel policy. Aria structures and enforces it." },
  { target: "policy-library-grid", placement: "top", eyebrow: "Policies",
    title: "The rulebook, live", body: "Every policy with its violation count. Toggle rules on and off, edit them, or drill straight into the expenses that triggered them." },
  { target: "policy-controls-button", placement: "left", eyebrow: "Policies",
    title: "Guardrails for the agent", body: "Controls decide what Aria may do alone — auto-approve limits, who signs off on exceptions, and where a human is always required." },

  /* — Platform — */
  { route: "/integrations", target: "plaid-section", placement: "bottom", eyebrow: "Platform",
    title: "Real bank feeds", body: "Connect corporate cards and banks through Plaid — transactions stream in and Aria matches them to receipts automatically. Plus an integrations marketplace for your ERP and HRIS." },
  { target: "topnav-global-search-button", placement: "bottom", eyebrow: "Platform",
    title: "⌘K everything", body: "The command palette jumps to any expense, employee or page from anywhere. Muscle memory friendly." },
  { route: "/settings", target: "settings-tabs", placement: "bottom", eyebrow: "Platform",
    title: "Tune the autonomy", body: "Set the auto-approve threshold, manage the team, name the workspace — and reseed the demo when you want a fresh start." },

  { outro: true, title: "That's the whole platform", body: "Receipts chased on autopilot, expenses filed and audited, policy enforced in plain English — one agent, fully accountable. Start on Communications and watch Aria work." },
];

const PAD = 8;
const TIP_W = 340;

export const DemoTour = () => {
  const [active, setActive] = useState(false);
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const [pos, setPos] = useState(null);
  const tipRef = useRef(null);
  const hadPosRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const start = () => { setRect(null); setPos(null); setIdx(0); setActive(true); };
    window.addEventListener("aria:start-demo", start);
    return () => window.removeEventListener("aria:start-demo", start);
  }, []);

  const step = STEPS[idx];
  const centered = !!(step.intro || step.outro) || !!rect?.missing;
  const end = useCallback(() => {
    closeAria();
    pressEsc();
    setActive(false); setRect(null); setPos(null);
  }, []);

  // navigate to the step's route, run its action, then measure the target.
  // rect/pos are intentionally NOT reset between steps — the tooltip slides.
  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    const run = async () => {
      if (step.route && location.pathname + location.search !== step.route) {
        navigate(step.route);
        await sleep(160);
      }
      if (step.action) {
        try { await step.action(); } catch (e) { /* keep touring */ }
        if (cancelled) return;
        await sleep(200);
      }
      const sel = step.selector || (step.target ? `[data-testid="${step.target}"]` : null);
      if (!sel) return;
      for (let i = 0; i < 40 && !cancelled; i++) {
        const el = document.querySelector(sel);
        if (el) {
          const b0 = el.getBoundingClientRect();
          const inView = b0.top >= 8 && b0.bottom <= window.innerHeight - 8 && b0.height > 0;
          if (!inView) {
            try { el.scrollIntoView({ block: "center", inline: "center" }); } catch (e) { /* noop */ }
            await sleep(140);
          } else {
            await sleep(30);
          }
          if (cancelled) return;
          const b = el.getBoundingClientRect();
          setRect({ top: b.top, left: b.left, width: b.width, height: b.height, forIdx: idx });
          return;
        }
        await sleep(90);
      }
      // target never appeared (e.g. data-dependent) — show the card centered instead
      if (!cancelled) setRect({ missing: true, forIdx: idx });
    };
    run();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, idx]);

  // position the tooltip AFTER it renders, clamped fully within the viewport
  useLayoutEffect(() => {
    if (!active || centered) { setPos(null); return; }
    if (!rect) { setPos(null); return; }
    if (rect.forIdx !== idx) return; // still measuring the new step — keep old pos
    const el = tipRef.current;
    if (!el) return;
    const tw = el.offsetWidth || TIP_W;
    const th = el.offsetHeight || 200;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const clampX = (x) => Math.min(Math.max(x, 16), Math.max(16, vw - tw - 16));
    const clampY = (y) => Math.min(Math.max(y, 16), Math.max(16, vh - th - 16));
    const place = step.placement || "bottom";
    let top;
    let left;
    if (place === "left" || place === "right") {
      top = clampY(rect.top);
      if (place === "left") { left = rect.left - tw - 14; if (left < 16) left = rect.left + rect.width + 14; }
      else { left = rect.left + rect.width + 14; if (left + tw > vw - 16) left = rect.left - tw - 14; }
      left = clampX(left);
    } else {
      left = clampX(rect.left);
      const above = rect.top - th - 14;
      const below = rect.top + rect.height + 14;
      if (place === "top") top = above >= 16 ? above : (below + th <= vh - 16 ? below : clampY(above));
      else top = below + th <= vh - 16 ? below : (above >= 16 ? above : clampY(below));
      top = clampY(top);
    }
    setPos({ top, left });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, idx, rect, centered]);

  // track whether a position was already painted (enables slide transition)
  useEffect(() => { hadPosRef.current = !!pos; }, [pos]);

  if (!active) return null;
  const isLast = idx === STEPS.length - 1;
  const pct = Math.round(((idx + 1) / STEPS.length) * 100);

  const card = (
    <div className="rounded-[16px] border border-[#242424] bg-[#0f0f0f] p-5 shadow-2xl">
      <div className="flex items-start justify-between">
        <AriaMark size={36} />
        <button onClick={end} data-testid="demo-end" className="text-[#6b6b6b] hover:text-white"><X size={16} /></button>
      </div>
      {step.eyebrow && <div className="mt-3 text-[11px] font-semibold uppercase tracking-wider text-[#7FAEF9]">{step.eyebrow}</div>}
      <div className={`${step.eyebrow ? "mt-1" : "mt-3"} text-[19px] font-semibold tracking-tight text-white`}>{step.title}</div>
      <p className="mt-2 text-[13px] leading-relaxed text-[#A3A3A3]">{step.body}</p>
      <div className="mt-4 flex items-center gap-2">
        {idx > 0 && (
          <button onClick={() => setIdx((v) => v - 1)} data-testid="demo-back"
            className="flex h-9 items-center gap-1.5 rounded-xl border border-[#2a2a2a] px-3 text-[12.5px] text-[#A3A3A3] hover:bg-[#161616] hover:text-white">
            <ArrowLeft size={14} /> Back
          </button>
        )}
        <button onClick={() => (isLast ? end() : setIdx((v) => v + 1))} data-testid={isLast ? "demo-finish" : "demo-next"}
          className="flex h-9 items-center gap-1.5 rounded-xl btn-gradient px-4 text-[12.5px] font-medium text-white">
          {isLast ? <>Done <Rocket size={14} /></> : <>Next <ArrowRight size={14} /></>}
        </button>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[10.5px] tabular-nums text-[#6b6b6b]" data-testid="demo-progress-count">{idx + 1} / {STEPS.length}</span>
          <div className="h-1 w-14 overflow-hidden rounded-full bg-[#242424]">
            <div className="h-full rounded-full bg-[#4F8EF7] transition-all duration-300" style={{ width: `${pct}%` }} data-testid="demo-progress-bar" />
          </div>
        </div>
      </div>
    </div>
  );

  const motionCard = (
    <motion.div key={idx} initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.2 }}>
      {card}
    </motion.div>
  );

  return (
    <div className="fixed inset-0 z-[300]" data-testid="demo-tour">
      {/* click blocker */}
      <div className="absolute inset-0" style={{ background: centered ? "rgba(0,0,0,0.82)" : "transparent" }} />
      {/* spotlight — slides between targets */}
      {!centered && rect && !rect.missing && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{
            opacity: 1,
            top: rect.top - PAD, left: rect.left - PAD,
            width: rect.width + PAD * 2, height: rect.height + PAD * 2,
          }}
          transition={{ type: "spring", stiffness: 260, damping: 30 }}
          className="pointer-events-none absolute rounded-xl"
          style={{ boxShadow: "0 0 0 9999px rgba(0,0,0,0.78)", border: "2px solid rgba(79,142,247,0.9)" }} />
      )}

      {centered ? (
        <div className="absolute inset-0 flex items-center justify-center px-4">
          <div className="w-[380px] max-w-[calc(100vw-2rem)]" data-testid="demo-tooltip">
            <AnimatePresence mode="wait">{motionCard}</AnimatePresence>
          </div>
        </div>
      ) : (
        <div ref={tipRef} data-testid="demo-tooltip"
          className="absolute w-[340px] max-w-[calc(100vw-2rem)]"
          style={{
            top: pos ? pos.top : -9999, left: pos ? pos.left : 0,
            visibility: pos ? "visible" : "hidden",
            transition: pos && hadPosRef.current
              ? "top 0.45s cubic-bezier(0.22, 1, 0.36, 1), left 0.45s cubic-bezier(0.22, 1, 0.36, 1)"
              : "none",
          }}>
          {motionCard}
        </div>
      )}
    </div>
  );
};
